/**
 * SessionManager - Manages therapeutic chat session lifecycle
 *
 * Responsibilities:
 * - Create new chat sessions via backend API (session-per-mode architecture)
 * - Track current session ID and mode
 * - Handle mode switching (creates new session, backend closes previous)
 * - Provide session state to other components
 *
 * Session-Per-Mode Architecture:
 * - Each mode switch creates a NEW session (not in-place update)
 * - Backend automatically closes previous sessions
 * - Haiku summaries are generated for closed sessions (fast path)
 * - Sonnet dual-track analysis runs for qualifying ADVICE sessions (slow path)
 *
 * Usage:
 *   import { sessionManager } from './core/SessionManager.js';
 *   await sessionManager.startSession('ADVICE');
 *   const sessionId = sessionManager.getCurrentSessionId();
 */

import { API } from './api.js';
import { eventBus } from './utils.js';

class SessionManager {
  constructor() {
    this.currentSessionId = null;
    this.currentMode = null;
    this.sessionNumber = null;
    this.currentStarred = false;
    this.currentSessionNoteId = null;
    // Dashboard privacy pre-arm (P10): in-memory only (a refresh clears it,
    // consistent with the button visibly resetting). Pushed here by
    // PrivacyModeManager, consumed exactly once by the dashboard-submit
    // lazy-creation path in ModeOrchestrator. Never derived from/synced with
    // an active session's privacy state — see setPendingPrivacyArm.
    this.pendingPrivacyArm = false;
  }

  /**
   * Set (or clear) the dashboard privacy pre-arm flag. Pure state — no API
   * call; the actual privacy flag is set server-side at session creation via
   * the `privacyMode: true` metadata carve-out (POST /sessions/new).
   * @param {boolean} armed
   */
  setPendingPrivacyArm(armed) {
    this.pendingPrivacyArm = armed === true;
  }

  /**
   * Read and clear the pending privacy arm flag atomically (consume-once).
   * @returns {boolean} True if privacy was armed
   */
  consumePendingPrivacyArm() {
    const armed = this.pendingPrivacyArm === true;
    this.pendingPrivacyArm = false;
    return armed;
  }

  /**
   * Start a new session (also handles mode switching)
   * Backend automatically closes any previous active sessions
   *
   * @param {string} mode - Session mode (ADVICE, DISCOVERY, TREATMENT, INTAKE)
   * @param {Object} contextParams - Optional mode-specific parameters
   * @returns {Promise<Object|null>} Session data or null on failure
   */
  async startSession(mode = 'ADVICE', contextParams = {}) {
    // Fail loudly: callers must be able to distinguish "session created" from
    // "rate-limited / network error". Returning null silently caused downstream
    // POSTs with sessionId=null, which corrupted the chat pipeline (500s).
    const result = await API.createSession(mode, contextParams);

    if (!result?.session?.id) {
      // Carry the server's own code/status through when there is one. Callers
      // branch on it — a guest's GUEST_ACCOUNT_REQUIRED opens the conversion
      // overlay, a 429 gets a retry affordance — and collapsing everything into
      // NO_SESSION_IN_RESPONSE made all of those indistinguishable.
      const err = new Error(result?.error || 'Session creation failed: No session in response');
      err.code = result?.code || 'NO_SESSION_IN_RESPONSE';
      err.status = result?.httpStatus ?? null;
      err.serverMetadata = result?.metadata ?? null;
      throw err;
    }

    const previousMode = this.currentMode;
    this.currentSessionId = result.session.id;
    this.currentMode = mode;
    this.sessionNumber = result.session.sessionNumber;
    this.currentStarred = result.session.starred === true;
    this.currentSessionNoteId = result.session.sessionNoteId || null;

    eventBus.emit('session:started', {
      sessionId: this.currentSessionId,
      mode: this.currentMode,
      previousMode,
      sessionNumber: this.sessionNumber,
      contextParams,
      // Backend-resolved default voice (entry-aware) for ADVICE sessions.
      // Emitted on `session:started` inside the shared payload (entangled with
      // other resume consumers); `stanceSource` + `attunementCompletedAt`
      // indicate whether the voice is *committed* or still a soft default.
      stance: result.session?.stance || null,
      stanceSource: result.session?.stanceSource || null,
      attunementCompletedAt: result.session?.attunementCompletedAt ?? null,
      closedSessions: result.closedSessions || [],
      // Birth-time privacy flag (P10 dashboard pre-arm), echoed back by the
      // creation route so it's server-verified rather than client-assumed.
      // Consumed by PrivacyModeManager's session-boundary reset (P9-pre) and
      // AnalyticsService's `privacy_mode` field.
      privacyMode: result.session?.privacyMode === true,
      starred: this.currentStarred,
      sessionNoteId: this.currentSessionNoteId
    });

    return result;
  }

  /**
   * End the current session.
   *
   * Local state is only cleared and `session:ended` is only emitted when the
   * backend confirms closure (`result.closed === true`) OR reports the session
   * was already closed (`SESSION_NOT_ACTIVE`). On network/HTTP failures we
   * leave `currentSessionId` intact so the next message still targets the live
   * backend session — preventing the desync where the UI thinks the session is
   * closed but the server still has it ACTIVE.
   *
   * The already-closed case is routine, not exceptional: TREATMENT sessions
   * close server-side the moment their arc reaches the conclusion group
   * (`MessageOrchestrator._closeAtTreatmentTerminus`), so "Return to dashboard"
   * regularly arrives here against a session that is already COMPLETED. Reading
   * that as a failure would skip the state clear and never emit `session:ended`
   * — silently stranding the sidebar Recents refresh, the analytics event, the
   * input's draft/create-menu reset, and ModeOrchestrator's teardown.
   *
   * @returns {Promise<boolean>} True if the session is closed (now or already)
   */
  async endSession() {
    if (!this.currentSessionId) {
      console.log('[OrientationClose:Frontend] SessionManager.endSession: no current session', {
        step: 'endSession_no_session'
      });
      return true; // No session to end
    }

    const sessionId = this.currentSessionId;
    console.log('[OrientationClose:Frontend] SessionManager.endSession: calling API.closeSession', {
      sessionId,
      currentMode: this.currentMode,
      step: 'endSession_api_call'
    });
    const result = await API.closeSession(sessionId);

    // A 400 SESSION_NOT_ACTIVE means the server already closed it — the session
    // IS ended, which is all any caller of this method wants to be true.
    const alreadyClosed = result?.code === 'SESSION_NOT_ACTIVE';

    if (result?.closed !== true && !alreadyClosed) {
      console.error('[OrientationClose:Frontend] SessionManager.endSession: backend did not confirm closure', {
        sessionId,
        code: result?.code || null,
        httpStatus: result?.httpStatus ?? null,
        result,
        step: 'endSession_not_confirmed'
      });
      return false;
    }

    console.log('[OrientationClose:Frontend] SessionManager.endSession: session closed', {
      sessionId,
      alreadyClosed,
      processingQueued: result?.processingQueued ?? null,
      step: 'endSession_confirmed'
    });

    this.currentSessionId = null;
    this.currentMode = null;
    this.sessionNumber = null;
    this.currentStarred = false;
    this.currentSessionNoteId = null;

    eventBus.emit('session:ended', { sessionId });

    return true;
  }

  /**
   * Get the current session ID
   * @returns {string|null} Current session ID or null if no active session
   */
  getCurrentSessionId() {
    return this.currentSessionId;
  }

  /**
   * Get the current session mode
   * @returns {string|null} Current mode or null if no active session
   */
  getCurrentMode() {
    return this.currentMode;
  }

  /**
   * Get the current session number
   * @returns {number|null} Session number or null if no active session
   */
  getSessionNumber() {
    return this.sessionNumber;
  }

  /** @returns {boolean} Effective star state for the active session. */
  getCurrentStarred() {
    return this.currentStarred;
  }

  /** @returns {string|null} Backing SessionNote id, when one exists. */
  getCurrentSessionNoteId() {
    return this.currentSessionNoteId;
  }

  /** @param {boolean} starred Update local optimistic star state. */
  setCurrentStarred(starred) {
    this.currentStarred = starred === true;
  }

  /**
   * Check if a session is currently active
   * @returns {boolean} True if session exists
   */
  isSessionActive() {
    return this.currentSessionId !== null;
  }

  /**
   * Enable privacy mode for the current session. Enable-only by design (no
   * un-set path) — flags the session server-side so private messages are
   * excluded from every read (transcript, sidebar, descriptor, extraction).
   *
   * Throws rather than swallowing errors so the caller (PrivacyModeManager)
   * can fail closed and distinguish a retryable network error from a 400 on
   * an already-closed session.
   *
   * @returns {Promise<boolean>} True if the server confirmed privacy mode is set
   * @throws {Error} On network failure or a non-2xx response (`err.code`/`err.httpStatus` set)
   */
  async setPrivacyMode() {
    if (!this.currentSessionId) {
      const err = new Error('Cannot set privacy mode: No active session');
      err.code = 'NO_ACTIVE_SESSION';
      throw err;
    }

    const result = await API.setSessionPrivacy(this.currentSessionId);

    if (result?.privacyMode === true) {
      eventBus.emit('session:privacyModeChanged', {
        sessionId: this.currentSessionId,
        privacyMode: true
      });
      console.log(`[SessionManager] Privacy mode enabled for session ${this.currentSessionId}`);
      return true;
    }

    return false;
  }

  /**
   * Same-click-mistake affordance: attempt to revert privacy mode if no
   * messages were sent since it was enabled. The session is left completely
   * unchanged (still ACTIVE, full history intact) either way — this never
   * closes or navigates away from anything; it either silently reverts or
   * silently does nothing, leaving the caller to fall back to the normal
   * exit-confirmation flow.
   *
   * @returns {Promise<boolean>} True if reverted (no activity since entry)
   */
  async tryCancelPrivacyMode() {
    if (!this.currentSessionId) return false;

    const result = await API.cancelSessionPrivacy(this.currentSessionId);
    if (result?.reverted === true) {
      eventBus.emit('session:privacyModeChanged', {
        sessionId: this.currentSessionId,
        privacyMode: false
      });
      console.log(`[SessionManager] Privacy mode cancelled (no activity) for session ${this.currentSessionId}`);
      return true;
    }

    return false;
  }

  /**
   * End current session and start a new one in the same mode
   * Used when exiting privacy mode to start a fresh public conversation
   *
   * @returns {Promise<Object|null>} New session data or null
   */
  async endSessionAndStartNew() {
    const previousMode = this.currentMode || 'ADVICE';

    // End current session
    await this.endSession();

    // Start new session in same mode as before
    const result = await this.startSession(previousMode);

    if (result) {
      console.log(`[SessionManager] Ended session and started new ${previousMode} session`);
    }

    return result;
  }

  /**
   * Attach to an existing server-side session by ID without creating a new one.
   * Used for resuming an in-progress INTAKE session across devices/browsers.
   * The server already has the session and its history; we just point local
   * state at it and emit `session:started` so the rest of the UI binds.
   *
   * @param {string} sessionId
   * @param {string} mode
   * @param {{ stance?: string|null, stanceSource?: string|null, attunementCompletedAt?: number|null }|null} [sessionMeta]
   *   Resolved session metadata (fetched by the caller for ADVICE resume) so the
   *   resumed stance is seeded into the `session:started` payload rather than
   *   defaulting to unset. Optional; null for INTAKE.
   * @returns {Promise<{session: {id: string, type: string}}|null>}
   */
  async attachToSession(sessionId, mode = 'INTAKE', sessionMeta = null) {
    if (!sessionId) return null;

    const previousMode = this.currentMode;
    this.currentSessionId = sessionId;
    this.currentMode = mode;
    this.sessionNumber = null;
    this.currentStarred = sessionMeta?.starred === true;
    this.currentSessionNoteId = sessionMeta?.sessionNoteId || null;

    eventBus.emit('session:started', {
      sessionId,
      mode,
      previousMode,
      sessionNumber: null,
      contextParams: { resumed: true },
      stance: sessionMeta?.stance ?? null,
      stanceSource: sessionMeta?.stanceSource ?? null,
      attunementCompletedAt: sessionMeta?.attunementCompletedAt ?? null,
      closedSessions: [],
      starred: this.currentStarred,
      sessionNoteId: this.currentSessionNoteId
    });

    return { session: { id: sessionId, type: mode } };
  }

  /**
   * Reset session state without making API calls.
   * Used when starting a fresh chat from the dashboard — the next startSession()
   * call will create a new backend session and close the previous one automatically.
   */
  resetSession() {
    this.currentSessionId = null;
    this.currentMode = null;
    this.sessionNumber = null;
    this.currentStarred = false;
    this.currentSessionNoteId = null;
  }

  /**
   * Switch mode - now creates a new session (session-per-mode architecture)
   * Backend automatically closes previous session and generates summaries
   *
   * @deprecated Direct mode switching is deprecated. Use startSession() for new architecture.
   * @param {string} newMode - New session mode
   * @returns {Promise<Object|null>} New session data or null
   */
  async switchMode(newMode) {
    console.log('[SessionManager] switchMode is now handled by startSession (session-per-mode)');

    // In session-per-mode architecture, mode switch = new session
    const result = await this.startSession(newMode);

    // For backward compatibility, emit mode-changed event
    if (result) {
      eventBus.emit('session:mode-changed', {
        sessionId: this.currentSessionId,
        previousMode: result.closedSessions?.[0]?.type || null,
        newMode: this.currentMode
      });
    }

    return result;
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();
